/// <reference types="vite/client" />
import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import type { ApiResponse, AuthResponse, Contact, Proposal, DetectedChange, SyncStatus, User, ImportResult, ModuleConfig, Bauvorhaben, BauvorhabenDetail, BauvorhabenContactRole, BauvorhabenUpdateResult, ContactBauvorhabenLink, GewerkPhase, Gewerk, GewerkStatus, AccordionPayload, Invoice, InvoiceDetailResponse, InvoiceComment, InvoiceStatus, InvoiceTransitionAction, InvoiceWritePayload, BauvorhabenRechnungen } from '@/types';

const API_URL = import.meta.env.VITE_API_URL || '/api';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add auth token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem("access_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiResponse<unknown>>) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    
    // Skip token-refresh logic for login-related endpoints
    const isAuthEndpoint = originalRequest.url?.match(/\/auth\/(login|verify-otp|request-otp)/);

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true;
      
      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (!refreshToken) {
          throw new Error('No refresh token');
        }
        
        const response = await axios.post<ApiResponse<AuthResponse['tokens']>>(
          `${API_URL}/auth/refresh`,
          { refresh_token: refreshToken }
        );
        
        const { access_token } = response.data.data;
        localStorage.setItem('access_token', access_token);
        
        originalRequest.headers.Authorization = `Bearer ${access_token}`;
        return api(originalRequest);
      } catch (refreshError) {
        // Clear auth and redirect to login
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  // Legacy password login (for admins)
  login: (email: string, password: string) =>
    api.post<ApiResponse<AuthResponse>>('/auth/login', { email, password }),
  
  // OTP/Magic Link authentication
  requestOtp: (email: string) =>
    api.post<ApiResponse<{ email_sent: boolean; email: string }>>('/auth/request-otp', { email }),
  
  verifyOtp: (email: string, otp: string) =>
    api.post<ApiResponse<AuthResponse>>('/auth/verify-otp', { email, otp }),
  
  refresh: (refresh_token: string) =>
    api.post<ApiResponse<{ access_token: string; token_type: string; expires_in: number }>>('/auth/refresh', { refresh_token }),
  
  logout: () => api.post<ApiResponse<void>>('/auth/logout'),
  
  changeOwnPassword: (currentPassword: string, newPassword: string) =>
    api.put<ApiResponse<void>>('/auth/change-password', { current_password: currentPassword, new_password: newPassword }),

  // Aktueller Benutzer inkl. effektiver Modul-Zugriffe — hält den
  // localStorage-User aktuell (Rolle/Module), ohne Re-Login.
  me: () => api.get<ApiResponse<{ user: User }>>('/auth/me'),
};

// Contacts API
export const contactsApi = {
  getAll: (page = 1, search = '', sort = 'name_asc', letter = '') =>
    api.get<ApiResponse<Contact[]>>(`/contacts?page=${page}&search=${encodeURIComponent(search)}&sort=${sort}&letter=${letter}`),
  
  getById: (id: number) =>
    api.get<ApiResponse<Contact>>(`/contacts/${id}`),
  
  create: (data: Partial<Contact>) =>
    api.post<ApiResponse<{ proposal_id: number; status: string }>>('/contacts', data),
  
  update: (id: number, data: Partial<Contact>) =>
    api.put<ApiResponse<{ proposal_id: number; status: string }>>(`/contacts/${id}`, data),
  
  delete: (id: number) =>
    api.delete<ApiResponse<{ proposal_id: number; status: string }>>(`/contacts/${id}`),
  
  bulkDelete: (contactIds: number[]) =>
    api.post<ApiResponse<{ proposal_ids: number[]; failed_ids: number[] }>>('/contacts/bulk-delete', { contact_ids: contactIds }),
};

// Bauvorhaben API
export const bauvorhabenApi = {
  getAll: (params?: { page?: number; per_page?: number; search?: string; status?: string; sort?: string; bauleiter?: string | number }) => {
    const query = new URLSearchParams({ page: String(params?.page ?? 1) });
    if (params?.per_page) query.set('per_page', String(params.per_page));
    if (params?.search) query.set('search', params.search);
    if (params?.status) query.set('status', params.status);
    if (params?.sort) query.set('sort', params.sort);
    if (params?.bauleiter) query.set('bauleiter', String(params.bauleiter));
    return api.get<ApiResponse<Bauvorhaben[]>>(`/bauvorhaben?${query.toString()}`);
  },

  /** Bauleiter-Filter-Optionen (nur tatsächlich referenzierte Benutzer). */
  getBauleiter: () =>
    api.get<ApiResponse<{ id: number; name: string }[]>>('/bauvorhaben/bauleiter'),

  getById: (id: number) =>
    api.get<ApiResponse<BauvorhabenDetail>>(`/bauvorhaben/${id}`),

  getByContact: (contactId: number) =>
    api.get<ApiResponse<ContactBauvorhabenLink[]>>(`/bauvorhaben/by-contact/${contactId}`),

  create: (data: Partial<Bauvorhaben>) =>
    api.post<ApiResponse<{ id: number }>>('/bauvorhaben', data),

  update: (id: number, data: Partial<Bauvorhaben>) =>
    api.put<ApiResponse<BauvorhabenUpdateResult>>(`/bauvorhaben/${id}`, data),

  remove: (id: number) =>
    api.delete<ApiResponse<void>>(`/bauvorhaben/${id}`),

  addContact: (id: number, data: { contact_id: number; role: BauvorhabenContactRole }) =>
    api.post<ApiResponse<{ link_id: number; address_proposals_created?: number }>>(`/bauvorhaben/${id}/contacts`, data),

  removeContact: (id: number, linkId: number) =>
    api.delete<ApiResponse<void>>(`/bauvorhaben/${id}/contacts/${linkId}`),

  // Gewerk-Checkliste (Akkordeon) je Bauvorhaben
  getGewerke: (id: number) =>
    api.get<ApiResponse<AccordionPayload>>(`/bauvorhaben/${id}/gewerke`),

  updateGewerkStatus: (id: number, gewerkId: number, status: GewerkStatus) =>
    api.put<ApiResponse<{ status: GewerkStatus }>>(`/bauvorhaben/${id}/gewerke/${gewerkId}`, { status }),

  addGewerkContact: (id: number, gewerkId: number, contactId: number) =>
    api.post<ApiResponse<{ link_id: number }>>(`/bauvorhaben/${id}/gewerke/${gewerkId}/contacts`, { contact_id: contactId }),

  removeGewerkContact: (id: number, gewerkId: number, linkId: number) =>
    api.delete<ApiResponse<void>>(`/bauvorhaben/${id}/gewerke/${gewerkId}/contacts/${linkId}`),
};

// Gewerk-Katalog (global, Verwaltungsseite)
export const gewerkeApi = {
  getAll: () =>
    api.get<ApiResponse<{ phases: GewerkPhase[] }>>('/gewerke'),

  create: (data: { name: string; phase_id: number }) =>
    api.post<ApiResponse<{ id: number }>>('/gewerke', data),

  update: (id: number, data: Partial<Pick<Gewerk, 'name' | 'sort_order' | 'is_active'> & { phase_id: number }>) =>
    api.put<ApiResponse<void>>(`/gewerke/${id}`, data),

  remove: (id: number) =>
    api.delete<ApiResponse<void>>(`/gewerke/${id}`),

  createPhase: (data: { name: string }) =>
    api.post<ApiResponse<{ id: number }>>('/gewerke/phases', data),

  updatePhase: (id: number, data: { name?: string; sort_order?: number }) =>
    api.put<ApiResponse<void>>(`/gewerke/phases/${id}`, data),

  removePhase: (id: number) =>
    api.delete<ApiResponse<void>>(`/gewerke/phases/${id}`),
};

// Proposals API

// Rechnungen API (Modul 'rechnungen')
export const invoicesApi = {
  getAll: (params?: {
    page?: number; search?: string; direction?: string; status?: string;
    bauvorhaben_id?: string | number; contact_id?: string | number;
    date_from?: string; date_to?: string; sort?: string;
  }) => {
    const query = new URLSearchParams({ page: String(params?.page ?? 1) });
    if (params?.search) query.set('search', params.search);
    if (params?.direction) query.set('direction', params.direction);
    if (params?.status) query.set('status', params.status);
    if (params?.bauvorhaben_id) query.set('bauvorhaben_id', String(params.bauvorhaben_id));
    if (params?.contact_id) query.set('contact_id', String(params.contact_id));
    if (params?.date_from) query.set('date_from', params.date_from);
    if (params?.date_to) query.set('date_to', params.date_to);
    if (params?.sort) query.set('sort', params.sort);
    return api.get<ApiResponse<Invoice[]>>(`/rechnungen?${query.toString()}`);
  },

  getById: (id: number) =>
    api.get<ApiResponse<InvoiceDetailResponse>>(`/rechnungen/${id}`),

  create: (data: InvoiceWritePayload) =>
    api.post<ApiResponse<{ id: number }>>('/rechnungen', data),

  update: (id: number, data: InvoiceWritePayload) =>
    api.put<ApiResponse<Invoice>>(`/rechnungen/${id}`, data),

  remove: (id: number) =>
    api.delete<ApiResponse<void>>(`/rechnungen/${id}`),

  /** Multipart-Upload — axios setzt den FormData-Boundary selbst, keinen Content-Type setzen. */
  uploadFile: (id: number, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<ApiResponse<{ file_path: string; file_original_name: string; file_size: number; file_mime: string }>>(`/rechnungen/${id}/file`, formData);
  },

  /** PDF als Blob laden (iframe braucht eine Blob-URL — kein Bearer im iframe-Request). */
  getFileBlob: (id: number) =>
    api.get<Blob>(`/rechnungen/${id}/file`, { responseType: 'blob' }),

  transition: (id: number, action: InvoiceTransitionAction, comment?: string) =>
    api.post<ApiResponse<{ status: InvoiceStatus | string }>>(`/rechnungen/${id}/transition`, { action, comment }),

  getComments: (id: number) =>
    api.get<ApiResponse<InvoiceComment[]>>(`/rechnungen/${id}/comments`),

  addComment: (id: number, comment: string, isInternal = false) =>
    api.post<ApiResponse<{ id: number }>>(`/rechnungen/${id}/comments`, { comment, is_internal: isInternal }),

  exportCsv: (params?: { direction?: string; status?: string; date_from?: string; date_to?: string }) => {
    const query = new URLSearchParams();
    if (params?.direction) query.set('direction', params.direction);
    if (params?.status) query.set('status', params.status);
    if (params?.date_from) query.set('date_from', params.date_from);
    if (params?.date_to) query.set('date_to', params.date_to);
    return api.get<ApiResponse<{ filename: string; content_base64: string; count: number }>>(`/rechnungen/export?${query.toString()}`);
  },

  confirmExport: (ids: number[]) =>
    api.post<ApiResponse<{ updated: number }>>('/rechnungen/export-confirm', { ids }),

  getByBauvorhaben: (id: number) =>
    api.get<ApiResponse<BauvorhabenRechnungen>>(`/bauvorhaben/${id}/rechnungen`),
};

// Import API
export const importApi = {
  previewVcf: (vcfContent: string) =>
    api.post<ApiResponse<{ contacts: Contact[]; total_count: number }>>("/import/preview", { vcf_content: vcfContent }),
  
  importVcf: (contacts: Contact[]) =>
    api.post<ApiResponse<ImportResult>>("/import/import", { contacts }),

  previewCsv: (csvContent: string) =>
    api.post<ApiResponse<{ contacts: Contact[]; total_count: number }>>("/import/preview-csv", { csv_content: csvContent }),
  
  importCsv: (contacts: Contact[]) =>
    api.post<ApiResponse<ImportResult>>("/import/import-csv", { contacts }),
};

export const proposalsApi = {
  getAll: (page = 1) =>
    api.get<ApiResponse<Proposal[]>>(`/proposals?page=${page}`),
  
  getMyProposals: (page = 1) =>
    api.get<ApiResponse<Proposal[]>>(`/proposals/my?page=${page}`),
  
  getById: (id: number) =>
    api.get<ApiResponse<Proposal>>(`/proposals/${id}`),
  
  getByContact: (contactId: number) =>
    api.get<ApiResponse<Proposal[]>>(`/proposals/contact/${contactId}`),
  
  create: (data: { action: string; contact_id?: number; contact_data: Partial<Contact> }) =>
    api.post<ApiResponse<{ proposal_id: number }>>('/proposals', data),
};

// Admin API
export const adminApi = {
  // Contact Proposals
  bulkApprove: (proposalIds: number[]) =>
    api.post<ApiResponse<{ approved_ids: number[] }>>("/admin/bulk-approve", { proposal_ids: proposalIds }),
  
  bulkReject: (proposalIds: number[]) =>
    api.post<ApiResponse<{ rejected_ids: number[] }>>("/admin/bulk-reject", { proposal_ids: proposalIds }),
  // Rejected-contacts memory (auto-clean from Outlook on next sync)
  listRejectedContacts: (params?: { page?: number; per_page?: number; user_email?: string; action?: 'create' | 'update' | 'delete' }) =>
    api.get<ApiResponse<any[]>>(`/admin/rejected-contacts?page=${params?.page ?? 1}&per_page=${params?.per_page ?? 50}${params?.user_email ? `&user_email=${encodeURIComponent(params.user_email)}` : ''}${params?.action ? `&action=${params.action}` : ''}`),

  forgetRejectedContacts: (ids: number[]) =>
    api.post<ApiResponse<{ deleted: number }>>('/admin/rejected-contacts/forget', { ids }),

  
  getPendingProposals: (page = 1) =>
    api.get<ApiResponse<Proposal[]>>(`/admin/pending-proposals?page=${page}`),
  
  approveProposal: (id: number, reviewNotes?: string) =>
    api.post<ApiResponse<void>>(`/admin/proposals/${id}/approve`, { review_notes: reviewNotes }),
  
  rejectProposal: (id: number, reviewNotes?: string) =>
    api.post<ApiResponse<void>>(`/admin/proposals/${id}/reject`, { review_notes: reviewNotes }),

  approveProposalWithChanges: (id: number, contactData: Partial<Contact>, reviewNotes?: string) =>
    api.post<ApiResponse<void>>(`/admin/proposals/${id}/approve-with-changes`, { contact_data: contactData, review_notes: reviewNotes }),

  mergeProposalIntoExisting: (id: number, targetContactId: number, mergeNotes?: string) =>
    api.post<ApiResponse<{
      proposal_id: number;
      target_contact_id: number;
      outlook_delete: boolean;
      outlook_attempted: boolean;
      mapping_dropped: boolean;
    }>>(`/admin/proposals/${id}/merge-into-existing`, {
      target_contact_id: targetContactId,
      merge_notes: mergeNotes,
    }),

  bulkMergeProposals: (items: { proposal_id: number; target_contact_id: number; merge_notes?: string }[]) =>
    api.post<ApiResponse<{
      results: Array<{
        proposal_id: number;
        target_contact_id: number;
        success: boolean;
        error?: string;
        data?: { outlook_delete: boolean; outlook_attempted: boolean; mapping_dropped: boolean };
      }>;
      partial: boolean;
    }>>('/admin/proposals/merge-into-existing/bulk', { items }),

  getDetectedChanges: (page = 1) =>
    api.get<ApiResponse<DetectedChange[]>>(`/admin/detected-changes?page=${page}`),
  
  approveDetectedChange: (id: number, reviewNotes?: string) =>
    api.post<ApiResponse<void>>(`/admin/detected-changes/${id}/approve`, { review_notes: reviewNotes }),
  
  rejectDetectedChange: (id: number, reviewNotes?: string) =>
    api.post<ApiResponse<void>>(`/admin/detected-changes/${id}/reject`, { review_notes: reviewNotes }),
  
  convertToProposal: (id: number) =>
    api.post<ApiResponse<{ proposal_id: number }>>(`/admin/detected-changes/${id}/convert`),
  
  getUsers: () =>
    api.get<ApiResponse<User[]>>('/admin/users'),
  
  createUser: (data: Partial<User> & { password?: string }) =>
    api.post<ApiResponse<{ user_id: number }>>('/admin/users', data),
  
  updateUser: (id: number, data: Partial<User>) =>
    api.put<ApiResponse<void>>(`/admin/users/${id}`, data),
  
  deleteUser: (id: number) =>
    api.delete<ApiResponse<void>>(`/admin/users/${id}`),
  
  getUserContactCount: (id: number) =>
    api.get<ApiResponse<{ count: number | null; message?: string }>>(`/admin/users/${id}/contact-count`),

  // Outlook-duplicates detection (admin-only diagnostic).
  // Returns one row per (global_uuid, user_email) that has >1 contact_mapping
  // rows, i.e. the same master contact is showing up as 2+ Outlook cards.
  // In addition to the DB-level duplicates we now also fingerprint-group
  // the live Outlook so we can find duplicates that exist in Outlook but
  // were never recorded in contact_mapping (e.g. created manually before
  // the user was added to sync, or produced by a previous buggy sync).
  getUserOutlookDuplicates: (id: number) =>
    api.get<ApiResponse<{
      duplicates: Array<{
        global_uuid: string;
        display_name: string | null;
        email_address: string | null;
        mapping_count: number;
        entries: Array<{ contact_mapping_id: number; ms_contact_id: string; last_pushed_at: string | null }>;
      }>;
      outlook_duplicates: Array<{
        fingerprint: string;
        display_name: string;
        card_count: number;
        ms_contact_ids: string[];
      }>;
      outlook_extra_cards: number;
      outlook_error: string | null;
      total_duplicate_mappings: number;
      total_duplicates: number;
      has_duplicates: boolean;
      message: string;
    }>>(`/admin/users/${id}/outlook-duplicates`),

  // Fire-and-forget dedupe: deletes the duplicate Outlook contact cards
  // and drops the orphan contact_mapping rows. Returns { log_id } so the
  // UI can poll the sync_logs row for progress.
  dedupeUserOutlookDuplicates: (id: number) =>
    api.post<ApiResponse<{
      log_id: number;
      status: string;
      candidates: number;
      poll_url: string;
    }>>(`/admin/users/${id}/dedupe-outlook-duplicates`, {}),
  
  exportUserContacts: (id: number) =>
    api.get<ApiResponse<{ filename: string; content: string; count: number }>>(`/admin/users/${id}/export-vcf`),

  wipeUserOutlookOnly: (id: number) =>
    api.post<ApiResponse<{
      log_id?: number;
      status?: string;
      sync_email?: string;
      mode?: string;
      poll_url?: string;
      user_id?: number;
      user_name?: string;
      outlook_email?: string;
      outlook_contacts_deleted?: number;
      outlook_delete_failed?: number;
      log?: string[];
    }>>(`/admin/users/${id}/wipe-outlook`, {}),

  wipeAndRestoreUserContacts: (id: number) =>
    api.post<ApiResponse<{
      log_id?: number;
      status?: string;
      sync_email?: string;
      poll_url?: string;
      user_id?: number;
      user_name?: string;
      outlook_email?: string;
      outlook_contacts_deleted?: number;
      outlook_delete_failed?: number;
      db_contacts_pushed?: number;
      db_create_failed?: number;
      log?: string[];
    }>>(`/admin/users/${id}/wipe-restore-contacts`, {}),
  pushMasterContactsToUser: (id: number) =>
    api.post<ApiResponse<{
      user_id: number;
      user_name: string;
      outlook_email: string;
      master_contacts_total: number;
      created: number;
      updated: number;
      failed: number;
      log: string[];
    }>>(`/admin/users/${id}/push-from-db`, {}),
  
  changePassword: (id: number, password: string) =>
    api.put<ApiResponse<void>>(`/admin/users/${id}/password`, { password }),
  
  // Event Proposals
  getSettings: () =>
    api.get<ApiResponse<{ shared_calendar_email: string | null; import_contacts_email: string | null; import_calendar_email: string | null; ms_graph_tenant_id: string | null; ms_graph_client_id: string | null; ms_graph_redirect_uri: string | null }>>('/admin/settings'),
  updateSettings: (data: { shared_calendar_email?: string; import_contacts_email?: string; import_calendar_email?: string }) =>
    api.put<ApiResponse<void>>('/admin/settings', data),

  // Module-Konfiguration (globaler Kill-Switch + Rollen-Standard)
  getModules: () =>
    api.get<ApiResponse<ModuleConfig[]>>('/admin/modules'),
  updateModule: (data: { module_key: string; is_enabled?: boolean; role_defaults?: Record<string, boolean> }) =>
    api.put<ApiResponse<void>>('/admin/modules', data),
  
  approveEventProposal: (id: number, reviewNotes?: string) =>
    api.post<ApiResponse<void>>(`/admin/event-proposals/${id}/approve`, { review_notes: reviewNotes }),
  
  rejectEventProposal: (id: number, reviewNotes?: string) =>
    api.post<ApiResponse<void>>(`/admin/event-proposals/${id}/reject`, { review_notes: reviewNotes }),
};

// Sync API
export const syncApi = {
  getStatus: () =>
    api.get<ApiResponse<SyncStatus>>('/sync/status'),
  
  getLogs: (page = 1, operationType?: string) =>
    api.get<ApiResponse<SyncStatus['recent_operations']>>(
      `/sync/logs?page=${page}${operationType ? `&operation_type=${encodeURIComponent(operationType)}` : ''}`
    ),

  // Poll a single sync_logs row by id. Returns the row with the parsed
  // details JSON. Used by the dedupe and push buttons that need to know
  // when a fire-and-forget background job is done.
  getLog: (id: number) =>
    api.get<ApiResponse<any>>(`/sync/logs/${id}`),
  
  trigger: (type: 'push' | 'pull' | 'full', cronToken: string) =>
    api.post<ApiResponse<void>>('/sync/trigger', { type, cron_token: cronToken }),
  
  test: (cronToken: string) =>
    api.post<ApiResponse<any>>('/sync/test', { cron_token: cronToken }),
  
  clearDelta: (userEmail?: string) =>
    api.post<ApiResponse<any>>('/sync/clear-delta', { user_email: userEmail }),
  
  purgeAndReset: () =>
    api.post<ApiResponse<any>>('/sync/purge-reset', {}),
  
  purgeDatabase: (password: string) =>
    api.post<ApiResponse<any>>('/sync/purge-database', { password }),
  
  runImport: () =>
    api.post<ApiResponse<{ contacts: any; calendar: any }>>('/sync/run-import', {}),
};

export default api;

// Calendar/Events API
import type { CalendarEvent, EventProposal } from '@/types';

export const calendarApi = {
  // Events
  getAll: (params?: { page?: number; start_date?: string; end_date?: string; event_type?: string }) => {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', params.page.toString());
    if (params?.start_date) query.set('start_date', params.start_date);
    if (params?.end_date) query.set('end_date', params.end_date);
    if (params?.event_type) query.set('event_type', params.event_type);
    return api.get<ApiResponse<CalendarEvent[]>>(`/events?${query.toString()}`);
  },
  
  getById: (id: number) =>
    api.get<ApiResponse<CalendarEvent>>(`/events/${id}`),
  
  create: (data: Partial<CalendarEvent>) =>
    api.post<ApiResponse<{ proposal_id: number; status: string }>>('/events', data),
  
  update: (id: number, data: Partial<CalendarEvent>) =>
    api.put<ApiResponse<{ proposal_id: number; status: string }>>(`/events/${id}`, data),
  
  delete: (id: number) =>
    api.delete<ApiResponse<{ proposal_id: number; status: string }>>(`/events/${id}`),
  
  getUpcoming: (days = 30) =>
    api.get<ApiResponse<CalendarEvent[]>>(`/events/upcoming?days=${days}`),
  
  getByMonth: (year: number, month: number) =>
    api.get<ApiResponse<CalendarEvent[]> & { pending_proposals: CalendarEvent[]; month: number; year: number }>(`/events/month?year=${year}&month=${month}`),
};

export const eventProposalsApi = {
  getAll: (page = 1) =>
    api.get<ApiResponse<EventProposal[]>>(`/event-proposals?page=${page}`),
  
  getMyProposals: (page = 1) =>
    api.get<ApiResponse<EventProposal[]>>(`/event-proposals/my?page=${page}`),
  
  getById: (id: number) =>
    api.get<ApiResponse<EventProposal>>(`/event-proposals/${id}`),
  
  approve: (id: number, reviewNotes?: string) =>
    api.post<ApiResponse<void>>(`/admin/event-proposals/${id}/approve`, { review_notes: reviewNotes }),
  
  reject: (id: number, reviewNotes?: string) =>
    api.post<ApiResponse<void>>(`/admin/event-proposals/${id}/reject`, { review_notes: reviewNotes }),
};
